// @flow

import './index.scss';
import * as React from 'react';
import classnames from 'classnames';
import FsLightbox from 'fslightbox-react';
import Icon from 'components/Icon';
import {
    CarouselProvider,
    Slider,
    Slide,
    Image,
    ButtonBack,
    ButtonNext
} from 'pure-react-carousel';
import { useTranslate } from '@insolus/react-i18n';
import YoutubeThumb from 'components/YoutubeThumb';

import type { Media } from 'stores/api/_types';

type Lightbox = {|
    toggler: boolean,
    slide: number
|};

type Props = {|
    items: Media[],
    isMobile: boolean
|};

const AdMedias = ({ items, isMobile }: Props): React.Node => {
    const { __ } = useTranslate();

    const [currentSlide, setCurrentSlide] = React.useState<number>(0);
    const [lightbox, setLightbox] = React.useState<Lightbox>({
        toggler : false,
        slide   : 1
    });

    const handleClick = React.useCallback(
        (index: number) => () => {
            setCurrentSlide(index);
        },
        []
    );

    const handleBack = React.useCallback(
        () => {
            if (currentSlide === 0) {
                return;
            }
            setCurrentSlide(prev => prev - 1);
        },
        [currentSlide]
    );

    const handleNext = React.useCallback(
        () => {
            if (currentSlide === (items.length - 1)) {
                return;
            }
            setCurrentSlide(prev => prev + 1);
        },
        [currentSlide, items]
    );

    const handleOpenLightbox = React.useCallback(
        (openSlide: number) => () => {
            setLightbox(prev => ({
                toggler : !prev.toggler,
                slide   : openSlide
            }));
        },
        []
    );

    if (items.length === 0) {
        return null;
    }

    return (
        <CarouselProvider
            className="Medias"
            naturalSlideWidth={100}
            totalSlides={items.length}
            visibleSlides={isMobile ? 1 : 4}
            isIntrinsicHeight
        >
            <FsLightbox
                toggler={lightbox.toggler}
                sources={items.map(({ paths, type, title }: Media): string => {
                    if (type === 'video/youtube') {
                        return `https://www.youtube.com/watch?v=${title}`;
                    }
                    return paths.original;
                })}
                slide={lightbox.slide + 1}
            />
            {items[currentSlide].type === 'video/youtube' ? (
                <YoutubeThumb
                    id={items[currentSlide].title}
                    number={0}
                    alt={__('video')}
                    className="CurrentMedia"
                    onClick={handleOpenLightbox(currentSlide)}
                    play
                />
            ) : (
                <Image
                    className="CurrentMedia"
                    src={items[currentSlide].paths.large}
                    alt={items[currentSlide].title}
                    onClick={handleOpenLightbox(currentSlide)}
                />
            )}
            <Slider className="Medias__slider">
                {items.map((item, index) => (
                    <Slide
                        key={item.filename}
                        index={index}
                        className="Medias__slide"
                        onClick={isMobile ? handleOpenLightbox(index) : handleClick(index)}
                    >
                        {item.type === 'video/youtube' ? (
                            <YoutubeThumb
                                id={item.title}
                                number={1}
                                alt={__('video')}
                                className={classnames('Medias__thumb', {
                                    'Medias__thumb--current': index === currentSlide
                                })}
                                play
                            />
                        ) : (
                            <Image
                                className={classnames('Medias__thumb', {
                                    'Medias__thumb--current': index === currentSlide
                                })}
                                src={isMobile ? item.paths.medium : item.paths.thumb}
                                alt={item.title}
                            />
                        )}
                    </Slide>
                ))}
            </Slider>
            <ButtonBack
                className="Medias__left"
                onClick={handleBack}
                disabled={currentSlide === 0}
            >
                <Icon type="chevronback" />
            </ButtonBack>
            <ButtonNext
                className="Medias__right"
                onClick={handleNext}
                disabled={currentSlide === (items.length - 1)}
            >
                <Icon type="chevronnext" />
            </ButtonNext>
        </CarouselProvider>
    );
};

export default AdMedias;
